import type { Seq, UserId } from '@maru/protocol'
import type { ReactNode } from 'react'
import type { EmojiLook } from './body_view.tsx'
import { locale_now, translate, useT } from './i18n.ts'
import { IconButton } from './kit/button.tsx'
import { Tooltip } from './kit/tooltip.tsx'

export interface Tally {
  emoji: string
  count: number
  mine: boolean
  who?: readonly UserId[]
}

/**
 * 알약에 손을 올리면 뜨는 글. 슬랙처럼 "가, 나 외 3명이 👍 반응". 조사는 마지막 이름의 받침으로
 * 고른다. `t` 는 화면이 넘긴다 — 없으면 지금 언어다.
 */
export function who_reacted(
  tally: Tally,
  name_of?: (u: UserId) => string | undefined,
  t: (ko: string, vars?: Readonly<Record<string, string | number>>) => string = (ko, vars) =>
    translate(locale_now(), ko, vars),
): string {
  const who = tally.who ?? []
  const emoji = tally.emoji
  if (who.length === 0) return t('{n}명이 {emoji} 반응', { n: tally.count, emoji })
  const names = who
    .slice(0, 3)
    .map((u) => name_of?.(u) ?? `#${u}`)
    .join(', ')
  const more = tally.count - who.slice(0, 3).length
  return more > 0
    ? t('{names} 외 {more}명이 {emoji} 반응', { names, more, emoji })
    : t('{names:이/가} {emoji} 반응', { names, emoji })
}

/** 누르면 바로 붙는 것들. 고르는 창을 열지 않아도 한 번에 반응할 수 있게. */
export const QUICK_EMOJI = ['👍', '❤️', '😂', '🎉', '👀', '✅'] as const

export interface ReactionBarProps {
  seq: Seq
  tallies: readonly Tally[]
  /**
   * 커스텀 이모지 이름 → 그림 주소. 반응 열쇠는 `:이름:` 형태도 되므로
   * 본문과 **같은 목록**으로 그린다 — 두 벌이면 방금 단 이모지가 한쪽에서만 뜬다.
   */
  emoji_of?: EmojiLook | undefined
  /** 누른 사람 이름. 없으면 `#번호` 로 적는다. */
  name_of?: ((user: UserId) => string | undefined) | undefined
  on_toggle(seq: Seq, emoji: string): void
  /** 알약 줄 끝의 "반응 추가" 단추(슬랙과 같은 자리). */
  more?: ReactNode
}

/** 알약 한 칸의 얼굴. 커스텀이면 그림, 아니면 글자 그대로. */
function Face({ emoji, emoji_of }: { emoji: string; emoji_of?: EmojiLook | undefined }) {
  const name = emoji.startsWith(':') && emoji.endsWith(':') ? emoji.slice(1, -1) : undefined
  const url = name === undefined ? undefined : emoji_of?.(name)
  if (url === undefined) return <span className="face">{emoji}</span>
  return <img className="emoji custom" src={url} alt={emoji} title={emoji} />
}

/**
 * 한 줄 아래의 이모지 알약들. 내가 누른 것은 테두리가 진하다.
 * 알약을 누르면 뗀다/붙인다 — 같은 손으로 둘 다 한다.
 */
export function ReactionBar(props: ReactionBarProps) {
  const { seq, tallies, on_toggle, emoji_of, name_of } = props
  const t = useT()
  if (tallies.length === 0) return null
  return (
    <div className="ReactionBar">
      {tallies.map((tally) => (
        // 누가 눌렀는지는 kit 풍선으로 — 네이티브 `title` 은 키보드로는 안 뜬다(G12).
        <Tooltip key={tally.emoji} content={who_reacted(tally, name_of, t)}>
          <button
            type="button"
            className={tally.mine ? 'pill mine' : 'pill'}
            aria-pressed={tally.mine}
            onClick={() => on_toggle(seq, tally.emoji)}
          >
            <Face emoji={tally.emoji} emoji_of={emoji_of} />
            <span className="n">{tally.count}</span>
          </button>
        </Tooltip>
      ))}
      {props.more}
    </div>
  )
}

/** 줄 위에 뜨는 빠른 반응 손. */
export function QuickReact({
  seq,
  on_toggle,
  tallies,
}: {
  seq: Seq
  on_toggle(seq: Seq, emoji: string): void
  /** 이 줄의 반응 — 내가 이미 누른 이모지는 눌린 채로 읽힌다(`aria-pressed`). */
  tallies?: readonly Tally[] | undefined
}) {
  const t = useT()
  return (
    <span className="QuickReact">
      {QUICK_EMOJI.map((e) => (
        <IconButton
          key={e}
          size="sm"
          className="quick"
          label={t('{emoji} 반응', { emoji: e })}
          pressed={tallies?.some((t) => t.emoji === e && t.mine) === true}
          icon={<span aria-hidden>{e}</span>}
          onClick={() => on_toggle(seq, e)}
        />
      ))}
    </span>
  )
}
